import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  commentThreadPosition,
  formatInZone,
  groupCommentThreads,
  type CommentAnchor,
  type CommentThread,
  type SceneDoc,
} from '@system-atlas/shared';

/** Entwurf eines neuen Kommentars: Anker + Klickpunkt (fuer die Popup-Position). */
export interface CommentDraft {
  anchor: CommentAnchor;
  world: { x: number; y: number };
}

interface CommentsOverlayProps {
  scene: SceneDoc;
  scale: number;
  stagePos: { x: number; y: number };
  viewport: { width: number; height: number };
  readOnly: boolean;
  openThreadKey: string | null;
  onOpenThread: (key: string | null) => void;
  draft: CommentDraft | null;
  onCancelDraft: () => void;
  onSubmitDraft: (text: string) => void;
  /** Antwort = weiterer Kommentar mit demselben Anker. */
  onReply: (anchor: CommentAnchor, text: string) => void;
  onSetThreadResolved: (key: string, resolved: boolean) => void;
}

/** Panel-Position: am Pin, aber innerhalb des Sichtbereichs gehalten. */
function clampPanel(p: { x: number; y: number }, viewport: { width: number; height: number }) {
  return {
    left: Math.max(8, Math.min(p.x, viewport.width - 300)),
    top: Math.max(8, Math.min(p.y, viewport.height - 220)),
  };
}

function DraftPopup({
  at,
  onCancel,
  onSubmit,
}: {
  at: { left: number; top: number };
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  return (
    <div
      data-testid="comment-draft"
      className="pointer-events-auto absolute z-20 w-72 rounded-lg border border-slate-300 bg-white p-2 shadow-lg dark:border-slate-600 dark:bg-slate-800"
      style={at}
    >
      <div className="pb-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
        {t('editor.comments.draftTitle')}
      </div>
      <textarea
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('editor.comments.placeholder')}
        rows={3}
        className="w-full resize-none rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
      />
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          {t('editor.comments.cancel')}
        </button>
        <button
          type="button"
          disabled={text.trim().length === 0}
          onClick={() => onSubmit(text.trim())}
          className="rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white disabled:opacity-40 dark:bg-slate-100 dark:text-slate-900"
        >
          {t('editor.comments.add')}
        </button>
      </div>
    </div>
  );
}

function ThreadPanel({
  thread,
  at,
  readOnly,
  onClose,
  onReply,
  onSetResolved,
}: {
  thread: CommentThread;
  at: { left: number; top: number };
  readOnly: boolean;
  onClose: () => void;
  onReply: (anchor: CommentAnchor, text: string) => void;
  onSetResolved: (key: string, resolved: boolean) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  return (
    <div
      data-testid="comment-panel"
      className="pointer-events-auto absolute z-20 w-72 rounded-lg border border-slate-300 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800"
      style={at}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-2 py-1 dark:border-slate-700">
        {!readOnly ? (
          <button
            type="button"
            onClick={() => onSetResolved(thread.key, !thread.resolved)}
            className="rounded-md px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
          >
            {thread.resolved ? t('editor.comments.reopen') : t('editor.comments.resolve')}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('editor.comments.close')}
          className="rounded-md px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
        >
          ✕
        </button>
      </div>
      <ul className="max-h-64 overflow-y-auto p-2 text-sm">
        {thread.comments.map((c) => (
          <li key={c.id} className="pb-2">
            <div className="text-xs text-slate-500 dark:text-slate-400">
              <span className="font-semibold text-slate-700 dark:text-slate-200">{c.authorName}</span>{' '}
              {formatInZone(c.createdAt, undefined, 'dd.LL.yyyy HH:mm')}
            </div>
            <div className="whitespace-pre-wrap break-words">{c.text}</div>
          </li>
        ))}
      </ul>
      {!readOnly && (
        <div className="border-t border-slate-200 p-2 dark:border-slate-700">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('editor.comments.placeholder')}
            rows={2}
            className="w-full resize-none rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
          />
          <div className="flex justify-end pt-1">
            <button
              type="button"
              disabled={text.trim().length === 0}
              onClick={() => {
                onReply(thread.anchor, text.trim());
                setText('');
              }}
              className="rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white disabled:opacity-40 dark:bg-slate-100 dark:text-slate-900"
            >
              {t('editor.comments.reply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Kommentar-Ebene (Kollaboration Phase 5, Spec Abschnitt 8) als DOM-Overlay ueber dem
 * Canvas: Pins an Objekten/Punkten, Klick oeffnet den Thread, "Erledigt" blendet den Pin
 * standardmaessig aus (Umschalter blendet erledigte wieder ein). DOM statt Konva:
 * klickbar/barrierearm, E2E-pruefbar und nie im PNG/PDF-Export (der exportiert nur die Stage).
 */
export function CommentsOverlay(props: CommentsOverlayProps) {
  const { t } = useTranslation();
  const [showResolved, setShowResolved] = useState(false);
  const threads = groupCommentThreads(props.scene.comments ?? []);
  const resolvedCount = threads.filter((th) => th.resolved).length;
  const toScreen = (p: { x: number; y: number }) => ({
    x: p.x * props.scale + props.stagePos.x,
    y: p.y * props.scale + props.stagePos.y,
  });
  const openThread = props.openThreadKey ? (threads.find((th) => th.key === props.openThreadKey) ?? null) : null;
  const openPos = openThread ? commentThreadPosition(openThread, props.scene.nodes) : null;
  return (
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
      {/* Pins (erledigte standardmaessig ausgeblendet) */}
      {threads.map((thread) => {
        if (thread.resolved && !showResolved) return null;
        const pos = commentThreadPosition(thread, props.scene.nodes);
        if (!pos) return null; // Anker-Knoten geloescht -> Pin ausblenden
        const screen = toScreen(pos);
        return (
          <button
            key={thread.key}
            type="button"
            data-testid="comment-pin"
            aria-label={t('editor.comments.pinLabel')}
            title={`${thread.comments[0]!.authorName}: ${thread.comments[0]!.text.slice(0, 80)}`}
            onClick={() => props.onOpenThread(thread.key === props.openThreadKey ? null : thread.key)}
            className={`pointer-events-auto absolute flex h-6 min-w-6 -translate-y-full items-center justify-center rounded-full rounded-bl-none border border-white bg-amber-500 px-1 text-[10px] font-bold text-white shadow-md hover:bg-amber-600 ${
              thread.resolved ? 'opacity-50' : ''
            }`}
            style={{ left: screen.x, top: screen.y }}
          >
            {thread.comments.length}
          </button>
        );
      })}
      {/* Umschalter fuer erledigte Kommentare (nur wenn es welche gibt) */}
      {resolvedCount > 0 && (
        <button
          type="button"
          data-testid="comments-resolved-toggle"
          onClick={() => setShowResolved((v) => !v)}
          className="pointer-events-auto absolute left-3 top-3 rounded-md border border-slate-300 bg-white/90 px-2 py-1 text-xs shadow-sm backdrop-blur hover:bg-white dark:border-slate-600 dark:bg-slate-800/90"
        >
          {showResolved ? t('editor.comments.hideResolved') : t('editor.comments.showResolved')} ({resolvedCount})
        </button>
      )}
      {/* Offener Thread */}
      {openThread && openPos && (
        <ThreadPanel
          key={openThread.key}
          thread={openThread}
          at={clampPanel(toScreen(openPos), props.viewport)}
          readOnly={props.readOnly}
          onClose={() => props.onOpenThread(null)}
          onReply={props.onReply}
          onSetResolved={props.onSetThreadResolved}
        />
      )}
      {/* Entwurf eines neuen Kommentars */}
      {props.draft && (
        <DraftPopup
          at={clampPanel(toScreen(props.draft.world), props.viewport)}
          onCancel={props.onCancelDraft}
          onSubmit={props.onSubmitDraft}
        />
      )}
    </div>
  );
}
